/**
 * MobileLogin Component for LOOPINO AI Platform
 * Mobile-optimized login/registration interface with glass morphism design
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import './MobileLogin.css';
import { useAuthContext } from '../../../context/AuthContext';
import ForgotPasswordModal from './ForgotPasswordModal.jsx';
import useLanguage from '../../../hooks/useLanguage';
import DesktopThreadsBackground from '../../desktop/DesktopLogin/DesktopThreadsBackground';
import loopinoLogo from '../../../assets/images/LOOPINO-logo-new.svg';
import MobileHeader from '../MobileHeader';
import { loadGoogleIdentityScript } from '../../../utils/googleIdentity';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const MobileLogin = ({ onLoginSuccess, onNavigateBack }) => {
  const [isLogin, setIsLogin] = useState(true);
  const heroThreadsColor = useMemo(() => [0.98, 0.92, 1], []);
  
  // Protected setter to prevent unauthorized state changes
  const setIsLoginProtected = useCallback((newValue, reason = 'unknown') => {
    console.log(`[MOBILE_LOGIN_STATE] Attempting to change isLogin from ${isLogin} to ${newValue}, reason: ${reason}`);
    setIsLogin(newValue);
  }, [isLogin]);
  
  // Debug: Track when isLogin changes
  useEffect(() => {
    console.log('[MOBILE_LOGIN_STATE] isLogin state changed to:', isLogin);
  }, [isLogin]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    username: ''
  });
  
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState(null);

  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';

  // Use the shared authentication context
  const {
    login: authLogin,
    loginWithGoogle,
    register,
    isLoading,
    error: authError,
    clearError,
    clearAuth
  } = useAuthContext();
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();
  
  // SECURITY: Wrap login function to prevent accidental calls during registration
  const login = useCallback((...args) => {
    if (!isLogin) {
      console.error('SECURITY VIOLATION: Login function called while in registration mode!');
      throw new Error('SECURITY: Login function called while in registration mode');
    }
    console.log('SECURITY: Login function call approved - currently in login mode');
    return authLogin(...args);
  }, [authLogin, isLogin]);

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    return score;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Calcola forza password per registrazione
    if (name === 'password' && !isLogin) {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    
    // Marca il campo come "toccato"
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    
    // Rimuovi l'errore quando l'utente inizia a digitare
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, formData[name]);
  };

  const validateField = (name, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    let error = '';
    
    switch (name) {
      case 'email':
        if (!trimmedValue) {
          error = isLogin
            ? t('login.validation.emailOrUsernameRequired')
            : t('login.validation.emailRequired');
        } else if (isLogin) {
          if (trimmedValue.length < 3) {
            error = t('login.validation.loginIdentifierMin');
          }
        } else if (!EMAIL_REGEX.test(trimmedValue)) {
          error = t('login.validation.emailInvalid');
        }
        break;
      case 'password':
        if (!value) {
          error = t('login.validation.passwordRequired');
        } else if (value.length < 8) {
          error = t('login.validation.passwordLength');
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          error = t('login.validation.passwordComposition');
        }
        break;
      case 'confirmPassword':
        if (!isLogin) {
          if (!value) {
            error = t('login.validation.confirmPasswordRequired');
          } else if (value !== formData.password) {
            error = t('login.validation.passwordMismatch');
          }
        }
        break;
      case 'firstName':
        if (!isLogin && !trimmedValue) {
          error = t('login.validation.firstNameRequired');
        } else if (!isLogin && trimmedValue.length < 2) {
          error = t('login.validation.firstNameLength');
        }
        break;
      case 'lastName':
        if (!isLogin && !trimmedValue) {
          error = t('login.validation.lastNameRequired');
        } else if (!isLogin && trimmedValue.length < 2) {
          error = t('login.validation.lastNameLength');
        }
        break;
      case 'username':
        if (!isLogin && !trimmedValue) {
          error = t('login.validation.usernameRequired');
        } else if (!isLogin && trimmedValue.length < 3) {
          error = t('login.validation.usernameLength');
        } else if (!isLogin && !/^[a-zA-Z0-9_]+$/.test(trimmedValue)) {
          error = t('login.validation.usernameCharacters');
        }
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
    
    return error === '';
  };

  const validateForm = () => {
    const fieldsToValidate = ['email', 'password'];
    if (!isLogin) {
      fieldsToValidate.push('firstName', 'lastName', 'username', 'confirmPassword');
    }
    
    let isValid = true;
    fieldsToValidate.forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });
    
    return isValid;
  };

  // Development bypass login handler
  const handleDevLogin = async () => {
    if (process.env.REACT_APP_DEV_MODE !== 'true') return;
    
    setIsProcessing(true);
    setErrors({});
    clearError();

    try {
      const devCredentials = {
        login: 'dev@loopino.com',
        password: 'dev-password',
        isDev: true
      };
      
      console.log('Attempting DEVELOPMENT LOGIN...');
      const response = await login(devCredentials);
      console.log('Development login successful:', response);
      
      if (response) {
        console.log('[DEV_LOGIN] Development login successful, proceeding with navigation');
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        throw new Error('Development login failed');
      }
    } catch (error) {
      console.error('Development login error:', error);
      setErrors({ 
        general: 'Development login failed: ' + error.message
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoogleLogin = useCallback(async () => {
    if (isProcessing || isLoading) {
      return;
    }

    setErrors({});
    clearError();
    setVerificationInfo(null);
    setIsProcessing(true);

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!clientId) {
      setErrors({ general: t('login.errors.googleMissingClientId') });
      setIsProcessing(false);
      return;
    }

    try {
      await loadGoogleIdentityScript();

      if (!window.google?.accounts?.id) {
        throw new Error(t('login.errors.googleUnavailable'));
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          if (!response?.credential) {
            setErrors({ general: t('login.errors.googleFailed') });
            setIsProcessing(false);
            return;
          }

          try {
            const authResponse = await loginWithGoogle(response.credential);

            if (authResponse?.user?.is_verified === false) {
              const pendingEmail = authResponse.user?.email;
              clearAuth();

              const verificationMessage = t('login.messages.unverified', { email: pendingEmail });
              setErrors({ general: verificationMessage });
              setVerificationInfo({
                type: 'unverified',
                email: pendingEmail,
                token: null
              });
              return;
            }

            if (onLoginSuccess) {
              onLoginSuccess();
            }
          } catch (error) {
            setErrors({ general: error.message || t('login.errors.googleFailed') });
          } finally {
            setIsProcessing(false);
          }
        }
      });

      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
          setErrors({ general: t('login.errors.googlePromptBlocked') });
          setIsProcessing(false);
        } else if (notification.isDismissedMoment?.()) {
          setIsProcessing(false);
        }
      });
    } catch (error) {
      setErrors({ general: error.message || t('login.errors.googleFailed') });
      setIsProcessing(false);
    }
  }, [clearAuth, clearError, isLoading, isProcessing, loginWithGoogle, onLoginSuccess, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Prevent form submission if already processing
    if (isProcessing) {
      console.log('Form submission blocked - already processing');
      return;
    }

    // Clear any previous errors and set processing state
    setErrors({});
    clearError();
    setVerificationInfo(null);
    setIsProcessing(true);

    // SECURITY: Log the exact form mode to prevent confusion
    console.log('Form submitted in mode:', isLogin ? 'LOGIN' : 'REGISTRATION');

    try {
      if (isLogin) {
        // SECURITY CHECK: Ensure we're actually in login mode
        if (!isLogin) {
          throw new Error('SECURITY: Login attempted while not in login mode');
        }
        
        // Login with backend API
        const loginIdentifier = formData.email.trim();
        const credentials = {
          login: loginIdentifier, // The API expects 'login' field (can be email or username)
          password: formData.password
        };
        
        console.log('Attempting LOGIN with identifier:', loginIdentifier);
        const response = await login(credentials);
        console.log('Login successful:', response);
        
        // Only proceed with navigation if we actually have a successful response
        if (response) {
          if (response.user?.is_verified === false) {
            const pendingEmail = response.user?.email || loginIdentifier;
            console.warn('[MOBILE_LOGIN_STATE] Login blocked - user not verified', {
              pendingEmail,
              userId: response.user?.id
            });

            clearAuth();

            const verificationMessage = t('login.messages.unverified', { email: pendingEmail });
            setErrors({
              general: verificationMessage
            });
            setVerificationInfo({
              type: 'unverified',
              email: pendingEmail,
              token: null
            });
            return;
          }

          console.log('[MOBILE_LOGIN_STATE] Login truly successful, proceeding with navigation');
          setVerificationInfo(null);
          if (onLoginSuccess) {
            onLoginSuccess();
          }
        } else {
          console.error('[MOBILE_LOGIN_STATE] Login response missing access token:', response);
          throw new Error('Login response missing required authentication data');
        }
      } else {
        // SECURITY CHECK: Ensure we're actually in registration mode
        if (isLogin) {
          throw new Error('SECURITY: Registration attempted while in login mode');
        }
        
        // Register with backend API
        const trimmedEmail = formData.email.trim();
        const trimmedUsername = formData.username.trim();
        const trimmedFirstName = formData.firstName.trim();
        const trimmedLastName = formData.lastName.trim();

        const userData = {
          email: trimmedEmail,
          username: trimmedUsername,
          password: formData.password,
          first_name: trimmedFirstName,
          last_name: trimmedLastName
        };
        
        console.log('Attempting REGISTRATION with email:', trimmedEmail);
        const response = await register(userData);

        // Check for successful registration by verifying response structure
        // Registration is successful if we have a user object or a 200/201 status
        const isSuccess = response && (
          response.user ||
          response._status === 200 ||
          response._status === 201 ||
          response.message?.includes('success')
        );

        if (isSuccess) {
          console.log('Registration successful:', response);

          // Switch to login mode and show verification message
          setIsLoginProtected(true, 'registration success');
          setFormData({
            email: trimmedEmail,
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            username: ''
          });
          setPasswordStrength(0);

          // Show success message aligned with backend response
          const successMessage = response.message || t('login.messages.registrationSuccess');
          setErrors({
            general: successMessage
          });

          setVerificationInfo({
            type: 'registration',
            email: trimmedEmail,
            token: isDevMode && response.verification_token ? response.verification_token : null
          });

          console.log('Registration completed successfully, switched to login mode');
        } else {
          // Handle unexpected response format
          console.log('Unexpected response format:', response);
          throw new Error('Errore durante la registrazione. Riprova.');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      
      setVerificationInfo(null);

      const rawMessage = error.message || (isLogin ? t('login.errors.loginInvalid') : t('login.errors.registrationGeneric'));
      const normalizedMessage = rawMessage.toLowerCase();

      let errorMessage = rawMessage;

      if (normalizedMessage.includes('rate limit') || normalizedMessage.includes('too many') || normalizedMessage.includes('429')) {
        errorMessage = t('login.errors.rateLimited');
      }

      if (!isLogin) {
        if (normalizedMessage.includes('email already exists') || normalizedMessage.includes('user with this email')) {
          errorMessage = t('login.errors.emailExists');
        } else if (normalizedMessage.includes('username already exists')) {
          errorMessage = t('login.errors.usernameExists');
        }

        console.log('Registration error - ensuring we stay in registration mode');
        console.log('SECURITY: Blocking any potential login attempts after registration failure');
      }

      const fieldErrors = {};

      if (isLogin) {
        if (normalizedMessage.includes('credential') || normalizedMessage.includes('login')) {
          fieldErrors.email = t('login.errors.fieldEmailInvalid');
        }
        if (normalizedMessage.includes('password')) {
          fieldErrors.password = t('login.errors.fieldPasswordInvalid');
        }
      } else {
        if (normalizedMessage.includes('email')) {
          fieldErrors.email = t('login.errors.fieldEmailHint');
        }
        if (normalizedMessage.includes('username')) {
          fieldErrors.username = t('login.errors.fieldUsernameInvalid');
        }
        if (normalizedMessage.includes('password')) {
          fieldErrors.password = t('login.errors.fieldPasswordInvalid');
        }
      }

      console.log('Setting error message:', errorMessage);
      setErrors({ 
        ...fieldErrors,
        general: errorMessage
      });
      
      // Make sure we don't redirect or change modes when there's an error
      console.log('Error occurred, staying on current form mode:', isLogin ? 'login' : 'registration');
    } finally {
      // Always clear processing state
      setIsProcessing(false);
    }
  };

  const clearErrorsAndToggle = () => {
    setErrors({});
    clearError();
    setVerificationInfo(null);
    setPasswordStrength(0);
    setTouchedFields({});
    setIsLoginProtected(!isLogin, 'clearErrorsAndToggle');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      username: ''
    });
  };

  const handleForgotPassword = () => {
    console.log('[MobileLogin] Opening forgot password modal');
    setForgotPasswordModalOpen(true);
  };

  const handleForgotPasswordSuccess = (email) => {
    console.log('[MobileLogin] Forgot password success for email:', email);
    // Could show additional success message or auto-fill email in login form
  };

  const toggleMode = () => {
    // Don't allow mode switching during processing
    if (isProcessing) {
      console.log('Blocking mode switch - currently processing');
      return;
    }

    // If there are errors, user needs to explicitly clear them first
    if (errors.general) {
      console.log('Cannot switch modes with active errors. User needs to clear them first.');
      return;
    }

    console.log('Mode switch allowed - switching from', isLogin ? 'login' : 'register', 'to', !isLogin ? 'login' : 'register');
    setIsLoginProtected(!isLogin, 'toggleMode');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      username: ''
    });
    setErrors({});
    // Clear any auth context errors when switching modes
    clearError();
    setVerificationInfo(null);
    setPasswordStrength(0);
    setTouchedFields({});
  };

  const generalMessage = errors.general || authError;
  const isGeneralSuccess = !!generalMessage && (generalMessage.includes('✅') || verificationInfo?.type === 'registration');
  const formTitle = isLogin ? t('login.title.login') : t('login.title.register');
  const formDescription = isLogin ? t('login.description.login') : t('login.description.register');
  const emailLabel = isLogin ? t('login.fields.emailLogin') : t('login.fields.emailRegister');
  const submitLabel = isLogin ? t('login.buttons.submitLogin') : t('login.buttons.submitRegister');
  const togglePrompt = isLogin ? t('login.toggle.promptLogin') : t('login.toggle.promptRegister');
  const toggleAction = isLogin ? t('login.toggle.register') : t('login.toggle.login');
  const toggleTitle = errors.general ? t('login.toggle.clearErrorHint') : undefined;
  const passwordStrengthLabel = (() => {
    if (passwordStrength <= 2) return t('login.passwordStrength.weak');
    if (passwordStrength === 3) return t('login.passwordStrength.good');
    if (passwordStrength === 4) return t('login.passwordStrength.strong');
    if (passwordStrength === 5) return t('login.passwordStrength.veryStrong');
    return '';
  })();
  const googleLabel = t('login.buttons.google');
  const dividerLabel = t('login.divider');
  const rememberMeLabel = t('login.buttons.rememberMe');
  const forgotPasswordLabel = t('login.buttons.forgotPassword');
  const backAriaLabel = t('login.aria.back');
  const clearMessageTitle = t('login.buttons.clearMessage');
  const footerCopy = t('login.footer.copy', { year: currentYear });
  const footerLinks = {
    privacy: t('login.footer.privacy'),
    terms: t('login.footer.terms'),
    cookies: t('login.footer.cookies'),
    legal: t('login.footer.legal'),
    help: t('login.footer.help')
  };

  return (
    <div className="mobile-login-container">
      {/* Aurora Background rimosso: attivo solo nella Home per performance */}

      <MobileHeader currentScreen="login" />

      {/* Back Button */}
      {onNavigateBack && (
        <button
          className="mobile-login-back-button"
          onClick={onNavigateBack}
          aria-label={backAriaLabel}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      {/* Scrollable Content */}
      <div className="mobile-login-content">
        <section className="mobile-login-hero" aria-live="polite">
          <div className="mobile-hero-glow" aria-hidden="true" />
          <div className="mobile-login-hero-card">
            <DesktopThreadsBackground
              color={heroThreadsColor}
              amplitude={1}
              distance={0.3}
              enableMouseInteraction={false}
              aria-hidden="true"
            />
            <div className="mobile-hero-content">
              <div className="mobile-hero-branding">
                <img src={loopinoLogo} alt="Loopino" />
              </div>
              <h2>Il tuo social media manager AI powered</h2>
              <p>
                Dall’idea alla pubblicazione, in un unico flusso: l’AI suggerisce format, rifinisce i copy, propone
                varianti creative e tiene aggiornato il calendario editoriale. Tu ci metti la visione, Loopino ti
                aiuta a restare costante.
              </p>
              <div className="mobile-hero-chips">
                <span>Bacheca idee e trend</span>
                <span>Calendario editoriale</span>
                <span>Copy &amp; visual con l&apos;AI</span>
              </div>
              <div className="mobile-hero-stats">
                <div>
                  <strong>1 flusso</strong>
                  <span>Idea → asset → calendario → pubblica</span>
                </div>
                <div>
                  <strong>Creatività illimitata</strong>
                  <span>nessuna sindrome del foglio bianco</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Login/Register Card */}
        <div className="mobile-login-card">

          <div className="mobile-card-header">
            <h1 className="mobile-card-title">
              {formTitle}
            </h1>
            <p className="mobile-card-description">
              {formDescription}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mobile-login-form">
            {generalMessage && (
              <div className={`mobile-error-message mobile-general-error ${isGeneralSuccess ? 'success' : ''}`}>
                <div className="mobile-general-message-content">
                  <span>{generalMessage}</span>
                  {verificationInfo?.email && (
                    <span className="mobile-general-subtext">
                      {verificationInfo.type === 'registration'
                        ? t('login.messages.verificationEmail', { email: verificationInfo.email })
                        : t('login.messages.verificationPending', { email: verificationInfo.email })}
                    </span>
                  )}
                  {isDevMode && verificationInfo?.token && (
                    <span className="mobile-general-dev-token">
                      {t('login.messages.devTokenLabel')} <code>{verificationInfo.token}</code>
                    </span>
                  )}
                </div>
                <button 
                  type="button" 
                  className="mobile-clear-error-button" 
                  onClick={() => {
                    setErrors(prev => ({ ...prev, general: '' }));
                    clearError();
                    setVerificationInfo(null);
                  }}
                  title={clearMessageTitle}
                >
                  ×
                </button>
              </div>
            )}

            {!isLogin && (
              <>
                <div className="mobile-form-field">
                  <div className={`mobile-input-container mobile-floating-label ${formData.firstName ? 'has-value' : ''} ${errors.firstName ? 'error' : ''}`}>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className="mobile-form-input"
                      placeholder=" "
                      autoComplete="given-name"
                      required
                    />
                    <label className="mobile-floating-label-text">{t('login.fields.firstName')}</label>
                    {formData.firstName && !errors.firstName && (
                      <div className="mobile-input-icon success">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.firstName && touchedFields.firstName && (
                    <span className="mobile-error-message">{errors.firstName}</span>
                  )}
                </div>

                <div className="mobile-form-field">
                  <div className={`mobile-input-container mobile-floating-label ${formData.lastName ? 'has-value' : ''} ${errors.lastName ? 'error' : ''}`}>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className="mobile-form-input"
                    placeholder=" "
                    autoComplete="family-name"
                    required
                  />
                  <label className="mobile-floating-label-text">{t('login.fields.lastName')}</label>
                    {formData.lastName && !errors.lastName && (
                      <div className="mobile-input-icon success">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.lastName && touchedFields.lastName && (
                    <span className="mobile-error-message">{errors.lastName}</span>
                  )}
                </div>

                <div className="mobile-form-field">
                  <div className={`mobile-input-container mobile-floating-label ${formData.username ? 'has-value' : ''} ${errors.username ? 'error' : ''}`}>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className="mobile-form-input"
                      placeholder=" "
                    autoComplete="username"
                    required
                  />
                    <label className="mobile-floating-label-text">{t('login.fields.username')}</label>
                    {formData.username && !errors.username && (
                      <div className="mobile-input-icon success">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  {errors.username && touchedFields.username && (
                    <span className="mobile-error-message">{errors.username}</span>
                  )}
                </div>
              </>
            )}

            <div className="mobile-form-field">
              <div className={`mobile-input-container mobile-floating-label ${formData.email ? 'has-value' : ''} ${errors.email ? 'error' : ''}`}>
                <input
                  type={isLogin ? 'text' : 'email'}
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className="mobile-form-input"
                  placeholder=" "
                  autoComplete={isLogin ? 'username' : 'email'}
                  required
                />
                <label className="mobile-floating-label-text">{emailLabel}</label>
                {formData.email && !errors.email && (
                  <div className="mobile-input-icon success">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                )}
              </div>
              {errors.email && touchedFields.email && (
                <span className="mobile-error-message">{errors.email}</span>
              )}
            </div>

            <div className="mobile-form-field">
              <div className={`mobile-input-container mobile-floating-label mobile-password-container ${formData.password ? 'has-value' : ''} ${errors.password ? 'error' : ''}`}>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className="mobile-form-input"
                  placeholder=" "
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  required
                />
                <label className="mobile-floating-label-text">{t('login.fields.password')}</label>
                <button
                  type="button"
                  className="mobile-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    {showPassword ? (
                      <path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z M8 5.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    ) : (
                      <>
                        <path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 5.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1 1l14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </>
                    )}
                  </svg>
                </button>
              </div>
              {errors.password && touchedFields.password && (
                <span className="mobile-error-message">{errors.password}</span>
              )}
              {!isLogin && formData.password && (
                <div className="mobile-password-strength">
                  <div className="mobile-strength-bar">
                    <div 
                      className={`mobile-strength-fill mobile-strength-${passwordStrength}`}
                      style={{ width: `${(passwordStrength / 5) * 100}%` }}
                    ></div>
                  </div>
                  <div className="mobile-strength-text">
                    {passwordStrengthLabel}
                  </div>
                </div>
              )}
            </div>

            {!isLogin && (
              <div className="mobile-form-field">
                <div className={`mobile-input-container mobile-floating-label mobile-password-container ${formData.confirmPassword ? 'has-value' : ''} ${errors.confirmPassword ? 'error' : ''}`}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className="mobile-form-input"
                    placeholder=" "
                    autoComplete="new-password"
                    required
                  />
                  <label className="mobile-floating-label-text">{t('login.fields.confirmPassword')}</label>
                  <button
                    type="button"
                    className="mobile-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex="-1"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      {showConfirmPassword ? (
                        <path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z M8 5.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      ) : (
                        <>
                          <path d="M1 8s3-5 7-5 7 5 7 5-3 5-7 5-7-5-7-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 5.5a2.5 2.5 0 110 5 2.5 2.5 0 010-5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M1 1l14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </>
                      )}
                    </svg>
                  </button>
                  {formData.confirmPassword && !errors.confirmPassword && formData.confirmPassword === formData.password && (
                    <div className="mobile-input-icon success">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
                {errors.confirmPassword && touchedFields.confirmPassword && (
                  <span className="mobile-error-message">{errors.confirmPassword}</span>
                )}
              </div>
            )}

            {isLogin && (
              <div className="mobile-form-options">
                <label className="mobile-checkbox-container">
                  <input type="checkbox" />
                  <span className="mobile-checkmark"></span>
                  {rememberMeLabel}
                </label>
                <button 
                  type="button" 
                  className="mobile-forgot-password"
                  onClick={handleForgotPassword}
                >
                  {forgotPasswordLabel}
                </button>
              </div>
            )}

            {/* Submit Button with Dev Login */}
            <div className="mobile-submit-section">
              <button
                type="submit"
                disabled={isLoading}
                className={`mobile-submit-button ${isLoading ? 'loading' : ''}`}
              >
                {isLoading ? (
                  <div className="mobile-loading-spinner"></div>
                ) : (
                  submitLabel
                )}
              </button>
              
              {/* DEV MODE: Easily removable for production */}
              {false && process.env.REACT_APP_DEV_MODE === 'true' && (
                <button 
                  type="button" 
                  className="mobile-dev-login-simple"
                  onClick={handleDevLogin}
                  disabled={isProcessing}
                  title="Development login bypass"
                >
                  dev login
                </button>
              )}
            </div>

            <div className="mobile-form-divider">
              <span>{dividerLabel}</span>
            </div>

            <button
              type="button"
              className="mobile-google-button"
              onClick={handleGoogleLogin}
              disabled={isLoading || isProcessing}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M19.99 10.187c0-.82-.069-1.417-.216-2.037H10.2v3.698h5.62c-.113.857-.69 2.182-1.98 3.06l-.02.124 2.88 2.239.2.02c1.83-1.691 2.88-4.17 2.88-7.104z" fill="#4285F4"/>
                <path d="M10.2 19.93c2.67 0 4.907-.87 6.543-2.37l-3.06-2.383c-.922.62-2.102.985-3.483.985-2.668 0-4.926-1.8-5.733-4.248l-.118.01-2.993 2.32-.04.111c1.617 3.2 4.94 5.575 8.884 5.575z" fill="#34A853"/>
                <path d="M4.467 11.914c-.216-.64-.336-1.32-.336-2.014s.12-1.374.336-2.014l-.006-.132-3.031-2.36-.099.047C.568 7.045 0 8.446 0 9.9s.568 2.855 1.331 4.259l3.136-2.445z" fill="#FBBC04"/>
                <path d="M10.2 3.853c1.914 0 3.206.828 3.943 1.521l2.878-2.826C15.1.985 12.87 0 10.2 0 6.256 0 2.933 2.375 1.331 5.641l3.03 2.36C5.169 5.653 7.427 3.853 10.2 3.853z" fill="#EB4335"/>
              </svg>
              {googleLabel}
            </button>

            <div className="mobile-form-toggle">
              <span>{togglePrompt}</span>
              <button 
                type="button" 
                onClick={errors.general ? clearErrorsAndToggle : toggleMode} 
                className="mobile-toggle-button"
                title={toggleTitle}
              >
                {toggleAction}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
          <div className="mobile-login-footer">
          <p>{footerCopy}</p>
          <div className="mobile-footer-links">
            <a href="/privacy">{footerLinks.privacy}</a>
            <a href="/terms">{footerLinks.terms}</a>
            <a href="/cookies">{footerLinks.cookies}</a>
            <a href="/legal">{footerLinks.legal}</a>
            <a href="mailto:support@loopino.com">{footerLinks.help}</a>
          </div>
        </div>

        {/* Forgot Password Modal */}
        <ForgotPasswordModal
          isOpen={forgotPasswordModalOpen}
          onClose={() => setForgotPasswordModalOpen(false)}
          onSuccess={handleForgotPasswordSuccess}
        />
      </div>
    </div>
  );
};

export default MobileLogin;
